import { resolveWriteClockStaleTime } from "@-/dev.improve.haniverse.lib.database.change";
import { describeFailureForUser } from "@-/dev.improve.haniverse.lib.error-classification";
import { isDatabaseClosingError } from "@-/dev.improve.haniverse.lib.database.engine";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { captureSentryException } from "@-/dev.improve.haniverse.lib.sentry";
import { instrumentQueryCachePerf } from "@-/dev.improve.haniverse.lib.sentry/perf";
import { MutationCache, QueryCache, type QueryClient } from "@-/com.npmjs.tanstack__react-query";
import { installDeadlineExceededReporter } from "@-/dev.improve.haniverse.lib.async-watchdog";
import { DeadlineBoundedQueryClient } from "#deadline-bounded-query-client";

export function createRootQueryClient(): QueryClient {
  installDeadlineExceededReporter();
  // Every query and mutation function is deadline-bounded (see `DeadlineBoundedQueryClient`).
  const queryClient = new DeadlineBoundedQueryClient({
    defaultOptions: {
      // Never PAUSE work on the OS's reachability guess. TanStack's default (`online`) parks every
      // query and mutation while `onlineManager` reports offline, which in this local-first app
      // includes every SQLite read and write: a stale or wrong offline signal (a Wi-Fi handoff, a
      // captive-portal probe, a path monitor that stopped reporting) left screens and actions on an
      // endless spinner, with `fetchStatus: "paused"` invisible to every stuck-fetch watchdog. With
      // `always`, local work runs, and a network read that truly cannot connect FAILS, surfacing its
      // error and retry. `onlineManager` still drives refetch-on-reconnect.
      mutations: { networkMode: "always" },
      queries: {
        networkMode: "always",
        // A failed query must surface its actual failure immediately. Automatic
        // re-execution makes deterministic data/query bugs look intermittent and
        // can strand Suspense screens in their loading fallback.
        retry: false,
        // Freshness is never derived from time, and never merely ASSUMED. A read
        // that declared database dependencies (via `withTableChangeVersion`) is
        // fresh only while it can be PROVEN current — i.e. no table it touched has
        // been written since the clock snapshot taken when it was last read. That
        // check is synchronous at fetch time, so unlike change-event invalidation
        // (delivered a microtask, or a native bridge hop, after the commit) it
        // cannot lose the race to a read that starts in that window. This is what
        // makes serving a stale entry structurally impossible rather than unlikely.
        //
        // Everything else — network reads, immutable content/static reads that
        // carry their own `staleTime` — keeps `Infinity`, so a re-mounted observer
        // still resolves synchronously from cache instead of re-issuing a redundant
        // read (the SQLite-connection-saturating refetch storm).
        // Individual queries therefore do NOT need their own `staleTime: Infinity`.
        //
        // NB: an INVALIDATED query is still refetched by any active observer AND by the next
        // `prefetchQuery` for its key. Query-option objects are deliberately fresh render snapshots;
        // effectful consumers such as `usePrefetchQueries` deduplicate them by TanStack's semantic
        // query-key hash so harmless identity churn cannot produce a refetch loop (see the deck-tab
        // prefetch-storm fix).
        staleTime: (query) => resolveWriteClockStaleTime(query),
      },
    },
    mutationCache: new MutationCache({
      onError: (error, variables, _context, mutation) => {
        // Always log the failure so it lands in the app logs regardless of
        // build variant. `captureSentryException` only forwards to Sentry (and
        // is disabled entirely in dev via `enabled: !__DEV__`), so without this
        // an errored mutation would otherwise be invisible in the logs.
        logger(import.meta.location).info(
          `Mutation failed (${JSON.stringify(mutation.options.mutationKey)}):`,
          error,
        );
        captureSentryException(error, {
          extra: {
            mutationKey: mutation.options.mutationKey,
            variables,
          },
          tags: {
            // Transient vs deterministic (schema drift, uncompilable SQL) vs unknown, so a backend
            // that is broken for every user groups apart from network noise in Sentry.
            "han.failure_kind": describeFailureForUser(error).kind,
            "tanstack_query.kind": "mutation",
          },
        });
      },
    }),
    queryCache: new QueryCache({
      onError: (error, query) => {
        // See the mutation note above — log every query failure so it shows up
        // in the app logs even when Sentry capture is a no-op (e.g. in dev).
        logger(import.meta.location).info(`Query failed (${query.queryHash}):`, error, {
          cause: error.cause,
        });
        // A READ that lost a race with a deliberate actor-connection close is
        // expected churn, not a defect: the sign-in/sign-out sync swap (and the
        // error-boundary recovery) closes the connection and then calls
        // `resetQueries()`, which re-runs exactly these queries against the
        // replacement handle. `cancelQueries()` can't abort a `queryFn` that is
        // already running, so a few in-flight reads reject this way on EVERY
        // swap — ~14 events per 7 users within 40 minutes of shipping, all of
        // them results that were about to be discarded anyway (HAN-V).
        //
        // Scoped deliberately to the query path. The same error is NOT benign
        // when it fails a user action outright — it once stranded a signed-in
        // user on the auth form by failing the post-sign-in navigation — so
        // mutations, and any capture from elsewhere, still report. Those get
        // fixed at the source (see `runActorSyncProvisioning`'s single-flight).
        if (isDatabaseClosingError(error)) {
          return;
        }
        captureSentryException(error, {
          extra: {
            failureCount: query.state.fetchFailureCount,
            fetchStatus: query.state.fetchStatus,
            hasData: query.state.data !== undefined,
            queryHash: query.queryHash,
            queryKey: query.queryKey,
          },
          tags: {
            "han.failure_kind": describeFailureForUser(error).kind,
            "tanstack_query.kind": "query",
          },
        });
      },
    }),
  });

  // Automatic performance tracing for EVERY query. Subscribing to the QueryCache
  // (TanStack Query's officially-supported observability surface) opens a Sentry
  // span the moment any query starts fetching and closes it when it settles — so
  // any query that would show a loading spinner or block a screen becomes a timed
  // span on the active navigation transaction, with no per-call-site wiring. Spans
  // are dropped unless tracing is sampled (staging always, production 10%), so this
  // is free in dev. The client lives for the whole app session, so we never need to
  // unsubscribe. Opt a query out with `meta: { perfIgnore: true }`.
  instrumentQueryCachePerf(queryClient.getQueryCache());

  return queryClient;
}
